<template>
  <div class="admin-dashboard">
    <!-- Stats Cards -->
    <div class="stats-grid">
      <div class="stat-card news-card">
        <div class="stat-header">
          <div class="stat-icon-wrapper">
            <i class="fas fa-newspaper"></i>
          </div>
          <div class="stat-trend positive">
            <i class="fas fa-arrow-up"></i>
          </div>
        </div>
        <div class="stat-body">
          <div class="stat-number">{{ newsCount }}</div>
          <div class="stat-label">Noticias Publicadas</div>
          <div class="stat-description">Total de artículos en el sitio</div>
        </div>
        <div class="stat-footer">
          <button @click="$emit('navigate', 'news')" class="stat-action">
            Ver todas <i class="fas fa-arrow-right"></i>
          </button>
        </div>
      </div>

      <div class="stat-card school-card">
        <div class="stat-header">
          <div class="stat-icon-wrapper">
            <i class="fas fa-graduation-cap"></i>
          </div>
          <div class="stat-trend positive">
            <i class="fas fa-arrow-up"></i>
          </div>
        </div>
        <div class="stat-body">
          <div class="stat-number">{{ schoolNewsCount }}</div>
          <div class="stat-label">Noticias de Escuela</div>
          <div class="stat-description">Contenido educativo publicado</div>
        </div>
        <div class="stat-footer">
          <button @click="$emit('navigate', 'school-news')" class="stat-action">
            Ver todas <i class="fas fa-arrow-right"></i>
          </button>
        </div>
      </div>

      <div class="stat-card total-card">
        <div class="stat-header">
          <div class="stat-icon-wrapper">
            <i class="fas fa-chart-line"></i>
          </div>
          <div class="stat-trend neutral">
            <i class="fas fa-minus"></i>
          </div>
        </div>
        <div class="stat-body">
          <div class="stat-number">{{ totalContent }}</div>
          <div class="stat-label">Contenido Total</div>
          <div class="stat-description">Todo el contenido disponible</div>
        </div>
        <div class="stat-footer">
          <button class="stat-action disabled">
            Resumen general
          </button>
        </div>
      </div>
    </div>

    <div class="dashboard-grid">
      <!-- Quick Actions -->
      <div class="dashboard-card quick-actions-card">
        <div class="card-header">
          <h2 class="card-title">
            <i class="fas fa-bolt"></i>
            Acciones Rápidas
          </h2>
        </div>
        <div class="card-body">
          <div class="actions-list">
            <button @click="$emit('navigate', 'news')" class="action-item">
              <div class="action-icon news">
                <i class="fas fa-plus"></i>
              </div>
              <div class="action-content">
                <div class="action-title">Nueva Noticia</div>
                <div class="action-description">Crear un nuevo artículo</div>
              </div>
              <div class="action-arrow">
                <i class="fas fa-chevron-right"></i>
              </div>
            </button>

            <button @click="$emit('navigate', 'school-news')" class="action-item">
              <div class="action-icon school">
                <i class="fas fa-plus"></i>
              </div>
              <div class="action-content">
                <div class="action-title">Nueva Noticia de Escuela</div>
                <div class="action-description">Publicar contenido educativo</div>
              </div>
              <div class="action-arrow">
                <i class="fas fa-chevron-right"></i>
              </div>
            </button>

            <button @click="$emit('navigate', 'school')" class="action-item">
              <div class="action-icon edit">
                <i class="fas fa-edit"></i>
              </div>
              <div class="action-content">
                <div class="action-title">Editar Página Escuela</div>
                <div class="action-description">Modificar contenido principal</div>
              </div>
              <div class="action-arrow">
                <i class="fas fa-chevron-right"></i>
              </div>
            </button>
          </div>
        </div>
      </div>

      <!-- Recent Activity -->
      <div class="dashboard-card activity-card">
        <div class="card-header">
          <h2 class="card-title">
            <i class="fas fa-clock"></i>
            Actividad Reciente
          </h2>
          <span class="activity-count">{{ recentActivity.length }} elementos</span>
        </div>
        <div class="card-body">
          <div v-if="recentActivity.length === 0" class="empty-state">
            <div class="empty-icon">
              <i class="fas fa-inbox"></i>
            </div>
            <p class="empty-text">No hay actividad reciente</p>
            <p class="empty-subtext">Comienza creando contenido</p>
          </div>

          <div v-else class="activity-list">
            <div
              v-for="item in recentActivity"
              :key="`${item.type}-${item.id}`"
              class="activity-item"
            >
              <div class="activity-icon" :class="`type-${item.type}`">
                <i :class="item.icon"></i>
              </div>
              <div class="activity-details">
                <div class="activity-title">{{ item.title }}</div>
                <div class="activity-meta">
                  <span class="activity-type-badge" :class="`badge-${item.type}`">
                    {{ item.typeLabel }}
                  </span>
                  <span class="activity-date">
                    <i class="fas fa-clock"></i>
                    {{ formatDate(item.createdAt) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  news: any[];
  schoolNews: any[];
}

const props = defineProps<Props>();
defineEmits<{
  navigate: [tab: string]
}>();

const newsCount = computed(() => props.news.length);
const schoolNewsCount = computed(() => props.schoolNews.length);
const totalContent = computed(() => newsCount.value + schoolNewsCount.value);

const recentActivity = computed(() => {
  const activities = [
    ...props.news.slice(0, 3).map(item => ({
      id: item.id,
      title: item.title,
      type: 'news',
      typeLabel: 'Noticia',
      icon: 'fas fa-newspaper',
      createdAt: item.createdAt
    })),
    ...props.schoolNews.slice(0, 3).map(item => ({
      id: item.id,
      title: item.title,
      type: 'school-news',
      typeLabel: 'Escuela',
      icon: 'fas fa-school',
      createdAt: item.createdAt
    }))
  ];

  return activities
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);
});

const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('es-ES', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};
</script>

<style scoped>
.admin-dashboard {
  width: 100%;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--spacing-6);
  margin-bottom: var(--spacing-8);
}

.stat-card {
  background: white;
  border-radius: var(--border-radius-xl);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: var(--spacing-6);
  transition: var(--transition-all);
  border: 1px solid var(--color-neutral-200);
  overflow: hidden;
  position: relative;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--color-primary-500), var(--color-primary-600));
}

.stat-card.news-card::before {
  background: linear-gradient(90deg, #3b82f6, #2563eb);
}

.stat-card.school-card::before {
  background: linear-gradient(90deg, #8b5cf6, #7c3aed);
}

.stat-card.total-card::before {
  background: linear-gradient(90deg, #10b981, #059669);
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
  border-color: var(--color-primary-300);
}

.stat-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-4);
}

.stat-icon-wrapper {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, var(--color-primary-100), var(--color-primary-200));
  border-radius: var(--border-radius-xl);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  color: var(--color-primary-700);
}

.news-card .stat-icon-wrapper {
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  color: #1e40af;
}

.school-card .stat-icon-wrapper {
  background: linear-gradient(135deg, #ede9fe, #ddd6fe);
  color: #6d28d9;
}

.total-card .stat-icon-wrapper {
  background: linear-gradient(135deg, #d1fae5, #a7f3d0);
  color: #047857;
}

.stat-trend {
  width: 32px;
  height: 32px;
  border-radius: var(--border-radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
}

.stat-trend.positive {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}

.stat-trend.neutral {
  background: rgba(107, 114, 128, 0.1);
  color: #6b7280;
}

.stat-body {
  margin-bottom: var(--spacing-4);
}

.stat-number {
  font-size: 2.5rem;
  font-weight: var(--font-weight-bold);
  color: var(--color-neutral-900);
  line-height: 1;
  margin-bottom: var(--spacing-2);
}

.stat-label {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-neutral-700);
  margin-bottom: var(--spacing-1);
}

.stat-description {
  font-size: var(--font-size-sm);
  color: var(--color-neutral-500);
}

.stat-footer {
  padding-top: var(--spacing-4);
  border-top: 1px solid var(--color-neutral-200);
}

.stat-action {
  background: none;
  border: none;
  color: var(--color-primary-600);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  transition: var(--transition-all);
  padding: 0;
}

.stat-action:hover:not(.disabled) {
  color: var(--color-primary-700);
  gap: var(--spacing-3);
}

.stat-action.disabled {
  color: var(--color-neutral-400);
  cursor: default;
}

/* Dashboard Grid */
.dashboard-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-6);
}

.dashboard-card {
  background: white;
  border-radius: var(--border-radius-xl);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--color-neutral-200);
  overflow: hidden;
}

.card-header {
  padding: var(--spacing-6);
  border-bottom: 1px solid var(--color-neutral-200);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(to bottom, white, var(--color-neutral-50));
}

.card-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-neutral-900);
  margin: 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
}

.card-title i {
  color: var(--color-primary-600);
}

.activity-count {
  font-size: var(--font-size-sm);
  color: var(--color-neutral-500);
  background: var(--color-neutral-100);
  padding: 0.25rem 0.75rem;
  border-radius: var(--border-radius-full);
  font-weight: var(--font-weight-medium);
}

.card-body {
  padding: var(--spacing-6);
}

/* Quick Actions */
.actions-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
}

.action-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-4);
  padding: var(--spacing-4);
  background: var(--color-neutral-50);
  border: 1px solid var(--color-neutral-200);
  border-radius: var(--border-radius-lg);
  cursor: pointer;
  transition: var(--transition-all);
  text-align: left;
  width: 100%;
}

.action-item:hover {
  background: var(--color-primary-50);
  border-color: var(--color-primary-300);
  transform: translateX(4px);
}

.action-icon {
  width: 48px;
  height: 48px;
  border-radius: var(--border-radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-lg);
  flex-shrink: 0;
}

.action-icon.news {
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  color: #1e40af;
}

.action-icon.school {
  background: linear-gradient(135deg, #ede9fe, #ddd6fe);
  color: #6d28d9;
}

.action-icon.edit {
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  color: #92400e;
}

.action-content {
  flex: 1;
  min-width: 0;
}

.action-title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-neutral-900);
  margin-bottom: var(--spacing-1);
}

.action-description {
  font-size: var(--font-size-sm);
  color: var(--color-neutral-600);
}

.action-arrow {
  color: var(--color-neutral-400);
  font-size: var(--font-size-sm);
  flex-shrink: 0;
}

.action-item:hover .action-arrow {
  color: var(--color-primary-600);
}

/* Activity List */
.activity-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
  max-height: 400px;
  overflow-y: auto;
}

.activity-list::-webkit-scrollbar {
  width: 6px;
}

.activity-list::-webkit-scrollbar-track {
  background: var(--color-neutral-100);
  border-radius: 3px;
}

.activity-list::-webkit-scrollbar-thumb {
  background: var(--color-neutral-300);
  border-radius: 3px;
}

.activity-item {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-3);
  padding: var(--spacing-3);
  border-radius: var(--border-radius-lg);
  background: var(--color-neutral-50);
  transition: var(--transition-all);
  border: 1px solid transparent;
}

.activity-item:hover {
  background: white;
  border-color: var(--color-primary-200);
}

.activity-icon {
  width: 40px;
  height: 40px;
  border-radius: var(--border-radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: var(--font-size-base);
}

.activity-icon.type-news {
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  color: #1e40af;
}

.activity-icon.type-school-news {
  background: linear-gradient(135deg, #ede9fe, #ddd6fe);
  color: #6d28d9;
}

.activity-details {
  flex: 1;
  min-width: 0;
}

.activity-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-neutral-900);
  margin-bottom: var(--spacing-2);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.activity-meta {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  flex-wrap: wrap;
}

.activity-type-badge {
  font-size: 0.75rem;
  font-weight: var(--font-weight-semibold);
  padding: 0.125rem 0.5rem;
  border-radius: var(--border-radius-sm);
}

.activity-type-badge.badge-news {
  background: #dbeafe;
  color: #1e40af;
}

.activity-type-badge.badge-school-news {
  background: #ede9fe;
  color: #6d28d9;
}

.activity-date {
  font-size: 0.75rem;
  color: var(--color-neutral-500);
  display: flex;
  align-items: center;
  gap: var(--spacing-1);
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: var(--spacing-12) var(--spacing-6);
}

.empty-icon {
  font-size: 3rem;
  color: var(--color-neutral-300);
  margin-bottom: var(--spacing-4);
}

.empty-text {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  color: var(--color-neutral-600);
  margin: 0 0 var(--spacing-2) 0;
}

.empty-subtext {
  font-size: var(--font-size-sm);
  color: var(--color-neutral-500);
  margin: 0;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .dashboard-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: 1fr;
    gap: var(--spacing-4);
  }

  .stat-card {
    padding: var(--spacing-5);
  }

  .stat-number {
    font-size: 2rem;
  }

  .dashboard-grid {
    gap: var(--spacing-4);
  }

  .card-header,
  .card-body {
    padding: var(--spacing-4);
  }
}

@media (max-width: 640px) {
  .stat-header {
    margin-bottom: var(--spacing-3);
  }

  .stat-icon-wrapper {
    width: 48px;
    height: 48px;
    font-size: 1.5rem;
  }

  .stat-number {
    font-size: 1.75rem;
  }

  .action-item {
    padding: var(--spacing-3);
  }

  .activity-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-1);
  }
}
</style>


